'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

import { useLegislativeContext } from '@/providers/LegislativeProvider'
import { getStateObjectFromSlug } from '@/utilities/getStateObject'

/**
 * Syncs the URL pathname with the LegislativeProvider state
 * This component should be placed in the bills layout to automatically sync
 * the provider state with the current route for bills pages only
 * (not the dashboard at /legislative-tracker)
 */
export const LegislativeSync = () => {
  const pathname = usePathname()
  const { setSelectedLegislativeTab, setSelectedState } = useLegislativeContext()

  useEffect(() => {
    // Only sync for bills routes, not the dashboard
    if (!pathname?.startsWith('/legislative-tracker/bills')) {
      return
    }

    // Skip syncing for individual bill pages (they include [slug] in the path)
    // Patterns:
    // - /legislative-tracker/bills/[scope]/[slug]
    // - /legislative-tracker/bills/[scope]/[sessionSlug]/[slug]
    if (pathname?.match(/\/legislative-tracker\/bills\/[^/]+\/[^/]+(\/[^/]+)?$/)) {
      // Extract scope from pathname for bill pages (optional sync)
      const match =
        pathname.match(/\/legislative-tracker\/bills\/([^/]+)\/[^/]+\/[^/]+$/) ||
        pathname.match(/\/legislative-tracker\/bills\/([^/]+)\/[^/]+$/)
      if (match) {
        const scope = match[1]
        if (scope === 'us') {
          setSelectedLegislativeTab('us')
          setSelectedState(undefined)
        } else if (scope === 'all') {
          setSelectedLegislativeTab('all-bills')
          setSelectedState(undefined)
        } else {
          // It's a state code
          const stateObject = getStateObjectFromSlug(scope)
          if (stateObject) {
            setSelectedLegislativeTab('state')
            setSelectedState(scope.toLowerCase())
          }
        }
      }
      return
    }

    // Handle all bills: /legislative-tracker/bills/all
    if (pathname === '/legislative-tracker/bills/all') {
      setSelectedLegislativeTab('all-bills')
      setSelectedState(undefined)
      return
    }

    // Handle scoped bills: /legislative-tracker/bills/[scope]
    const scopedMatch = pathname?.match(/^\/legislative-tracker\/bills\/([^/]+)$/)
    if (scopedMatch) {
      const scope = scopedMatch[1]

      if (scope === 'us') {
        setSelectedLegislativeTab('us')
        setSelectedState(undefined)
      } else if (scope === 'all') {
        setSelectedLegislativeTab('all-bills')
        setSelectedState(undefined)
      } else {
        // It's a state code (e.g., 'ca', 'ma')
        const stateObject = getStateObjectFromSlug(scope)
        if (stateObject) {
          setSelectedLegislativeTab('state')
          setSelectedState(scope.toLowerCase())
        }
      }
    }
  }, [pathname, setSelectedLegislativeTab, setSelectedState])

  return null
}
